import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import QueryBuilder from './pages/QueryBuilder';
import AdminDashboard from './pages/AdminDashboard';
import ConfigSchemaPage from './pages/ConfigSchemaPage';
import AccountsPage from './pages/AccountsPage';
import { RequireRole } from './components/RequireRole';
import { useAuthStore } from './store/authStore';
import { useLogout } from './api/authApi';
import { useIsSuperAdmin } from './hooks/useRole';
import './App.css';

function UserMenu() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const authLogout = useAuthStore((state) => state.logout);
  const logoutMutation = useLogout();

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      // Client-side logout below still clears local session even if the request fails
    }
    authLogout();
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="navbar-user">
      <span className="navbar-user-info">
        {user.name}
        {user.role && <span className="navbar-user-role">{user.role.replace('_', ' ')}</span>}
      </span>
      <button
        type="button"
        className="navbar-logout-button"
        onClick={handleLogout}
        disabled={logoutMutation.isPending}
      >
        {logoutMutation.isPending ? 'Logging out...' : 'Log out'}
      </button>
    </div>
  );
}

function App() {
  const location = useLocation();
  const isSuperAdmin = useIsSuperAdmin();

  return (
    <div className="app-wrapper">
      <nav className="navbar">
        <div className="navbar-container">
          <Link
            to="/admin"
            className="trustflight-heading navbar-brand"
          >
            Integration Platform
          </Link>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
            {isSuperAdmin && (
              <Link
                to="/query"
                className={location.pathname === '/query' ? 'navbar-link-active' : 'navbar-link'}
              >
                Query Builder
              </Link>
            )}
            <Link
              to="/admin"
              className={location.pathname === '/admin' ? 'navbar-link-active' : 'navbar-link'}
            >
              Admin
            </Link>
            {isSuperAdmin && (
              <Link
                to="/admin/accounts"
                className={location.pathname.startsWith('/admin/accounts') ? 'navbar-link-active' : 'navbar-link'}
              >
                Accounts
              </Link>
            )}
            <UserMenu />
          </div>
        </div>
      </nav>

      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/admin" replace />} />
          <Route
            path="/query"
            element={
              <RequireRole allowed={['super_admin']}>
                <QueryBuilder />
              </RequireRole>
            }
          />
          {/* Legacy document editor URLs — document access now lives in the
              QueryBuilder edit modal and the admin config pages. */}
          <Route path="/document/*" element={<Navigate to="/query" replace />} />
          <Route path="/admin" element={<AdminDashboard />} />
          {/* Retired sync-data detail page — stale bookmarks land on the dashboard. */}
          <Route path="/admin/config/:configId" element={<Navigate to="/admin" replace />} />
          <Route
            path="/admin/config/:configId/schema"
            element={
              <RequireRole allowed={['super_admin']}>
                <ConfigSchemaPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/accounts"
            element={
              <RequireRole allowed={['super_admin']}>
                <AccountsPage />
              </RequireRole>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

export default App; 